import classNames from "classnames";
import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CappedModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/CappedModel";
import { FlexiModel } from "../../../../../../../Wrap/Models/Domains/Drawdown/FlexiModel";
import { PensionProtectionDetails } from "../../../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { processTypeEnum } from "../../Resource/Contents";
import {
    calculateMaximumIncomePaymentForFlexiTargetForExistingArrangement,
    calculateMaximumIncomePaymentForFutureYearForExistingArrangement,
} from "./Helper/IncomePaymentHelper";
import { IncomeArrangementType, IncomeArrangementTypeMaps } from "./Resource/Content";
import { TakeFullValue } from "./TakeFullValue";

export interface CappedFlexiTableProps {
    data: any[];
    type: IncomeArrangementType;
    cssWrapper?: string;
    onChange: (index: number, key: string, newValue: any) => void;
    drawdown?: DrawdownModel;
    processType?: processTypeEnum;
    pensionProtection?: PensionProtectionDetails;
}

interface Column<T = any> {
    title: string;
    key: string;
    render?: (item: T, index: number, key: string) => React.ReactNode;
}

function getColumns(props: CappedFlexiTableProps): Column[] {
    if (props.type == IncomeArrangementType.Capped) {
        const cappedColumns: Column<CappedModel>[] = [
            {
                title: "From",
                key: "From",
            },
            {
                title: "Amount",
                key: "Amount"
            },
            {
                title: "Type",
                key: "Type",
            },
            {
                title: "GAD Maximum",
                key: "GadMaximum",
            },
            {
                title: "Taxable",
                key: "Taxable",
                render: (item) => {
                    return item.Taxable ? "Yes" : "No";
                }
            },
            {
                title: "Income taken to date",
                key: "IncomeTakenToDate",
            },
            {
                title: "Next years GAD Maximum",
                key: "NextYearsGadMaximum",
            },
            {
                title: "Last mandatory review",
                key: "LastMandatoryReview",
                render: (item) => {
                    return JsonDateParser.getFormatDate(item.LastMandatoryReview, "dd-MM-yyyy")
                }
            },
            {
                title: "Maximum income payment for remainder of pension year",
                key: "MaximumIncomePaymentForRemainderOfPensionYear",
                render: (item) => {
                    const result = calculateMaximumIncomePaymentForFutureYearForExistingArrangement(
                        props?.processType,
                        item,
                        props?.drawdown?.Income
                    );
                    return MoneyDisplayHelper.FromatMoneyDisplay(result);
                },
            },
            {
                title: "Maximum income payment for future pension years",
                key: "MaximumIncomePaymentForFuturePensionYears",
                render: (item) => {
                    const result = calculateMaximumIncomePaymentForFutureYearForExistingArrangement(
                        props?.processType,
                        item,
                        props?.drawdown?.Income
                    );
                    return MoneyDisplayHelper.FromatMoneyDisplay(result);
                },
            },
            {
                title: "Income per payment for current pension year",
                key: "IncomePerPaymentForCurrentPensionYear",
                render: (item, index, key) => {
                    const value = item.IncomePerPaymentForCurrentPensionYear;
                    const maxValue = item.MaximumIncomePaymentForRemainderOfPensionYear;
                    return (
                        <MoneyTextBoxEditor
                            cssWrapper="control-size-245"
                            value={value}
                            currency={CurrencyCode.GBP}
                            onChange={(newValue) => {
                                props.onChange(index, key, newValue);
                            }}
                            maxNumberOfDecimalPlaces={{
                                errorMessage: "only allow two decimal places",
                                value: 2,
                            }}
                            validationRules={[
                                {
                                    errorMessage:
                                        "Value cannot be greater than maximum income payment for remainder of current pension year",
                                    validator: (v) => v <= maxValue,
                                },
                                {
                                    errorMessage: "Value can not be less than 0",
                                    validator: (v) => v >= 0,
                                },
                            ]}
                        />
                    );
                },
            },
            {
                title: "Income per payment for future pension years",
                key: "IncomePerPaymentForFuturePensionYears",
                render: (item, index, key) => {
                    const maxValue = item.MaximumIncomePaymentForFuturePensionYears;
                    return (
                        <MoneyTextBoxEditor
                            cssWrapper="control-size-245"
                            value={item.IncomePerPaymentForFuturePensionYears}
                            currency={CurrencyCode.GBP}
                            onChange={(newValue) => {
                                props.onChange(index, key, newValue);
                            }}
                            maxNumberOfDecimalPlaces={{
                                errorMessage: "only allow two decimal places",
                                value: 2,
                            }}
                            validationRules={[
                                {
                                    errorMessage:
                                        "Value cannot be greater than maximum income payment for future pension years",
                                    validator: (v) => v <= maxValue,
                                },
                                {
                                    errorMessage: "Value can not be less than 0",
                                    validator: (v) => v >= 0,
                                },
                            ]}
                        />
                    );
                },
            },
        ];
        return cappedColumns;
    }
    const flexiColumns: Column<FlexiModel>[] = [
        {
            title: "From",
            key: "From",
        },
        {
            title: "Amount",
            key: "Amount",
            render: (item) => {
                return MoneyDisplayHelper.FromatMoneyDisplay(item.Amount);
            }
        },
        {
            title: "Type",
            key: "Type",
        },
        {
            title: "Maximum income payment per payment",
            key: "MaximumIncomePerPayment",
            render: (item) => {
                const value = calculateMaximumIncomePaymentForFlexiTargetForExistingArrangement(
                    props?.processType,
                    item,
                    props?.drawdown,
                    props?.pensionProtection
                );
                return MoneyDisplayHelper.FromatMoneyDisplay(value);
            },
        },
        {
            title: "Income per payment",
            key: "IncomePerPayment",
            render: (item, index, key) => {
                const value = item.IncomePerPayment;
                const maxValue = item.MaximumIncomePerPayment;
                return (
                    <TakeFullValue
                        currentValue={value}
                        maxValue={maxValue}
                        onChange={(newValue) => props.onChange(index, key, newValue)}
                    />
                );
            },
        },
    ];
    return flexiColumns;
}

export const CappedFlexiTable = (props: CappedFlexiTableProps) => {
    const columns = getColumns(props);
    const cssWrapper = classNames("one-off-income-table", props.cssWrapper);

    return (
        <>
            <h3 className="mb-11">{IncomeArrangementTypeMaps[props.type]}</h3>
            <table className={cssWrapper}>
                <tbody>
                    {columns.map((row, index) => {
                        return (
                            <tr key={row.key + index}>
                                <td className="text-left"> {<h4 className="mb-11">{row.title}</h4>}</td>
                                {props.data.map((item, index) => {
                                   if (row.render) {
                                    return <td key={row.key}>{row.render(item, index, row.key)}</td>;
                                } else {
                                    return <td key={row.key}>{item[row.key]}</td>;
                                }
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </>
    );
};
